import { getAuthToken, getAuthHeaders } from '@/utils/auth';
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Building2, Bed, Bath, Maximize, MapPin, Home, Wifi, Car, 
  Dumbbell, Trees, Shield, ChevronLeft, ChevronRight, X, 
  Heart, Share2, Calendar, Phone, Mail, Clock, Star, 
  Sparkles, ZoomIn, Check, ArrowLeft, Eye
} from 'lucide-react';
import axios from 'axios';
import Nav from '@/components/Nav';
import { toast } from 'sonner';
import { VirtualTourPreview, VirtualTourButton } from '../components/VirtualTour';
import { useCurrency } from '@/context/CurrencyContext';
import PriceDisplay from '@/components/PriceDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lightbox Gallery Component
const ImageGallery = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  const defaultImages = [
    'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200',
    'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200',
    'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200',
  ];

  const galleryImages = images?.length > 0 ? images : defaultImages;

  const nextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
  }, [galleryImages.length]);

  const prevImage = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  }, [galleryImages.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, nextImage, prevImage]);

  return (
    <>
      {/* Main Gallery */}
      <div className="relative group">
        {/* Main Image */}
        <div 
          className="aspect-[16/9] overflow-hidden rounded-2xl cursor-pointer relative"
          onClick={() => setLightboxOpen(true)}
        >
          <img
            src={galleryImages[currentIndex]}
            alt={`${title} - Image ${currentIndex + 1}`}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm px-4 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2">
            <ZoomIn className="w-4 h-4 text-white" />
            <span className="text-white text-sm">Click to enlarge</span>
          </div>
          <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm px-4 py-2 rounded-lg">
            <span className="text-white text-sm">{currentIndex + 1} / {galleryImages.length}</span>
          </div>
        </div>

        {/* Navigation Arrows */}
        {galleryImages.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail Strip */}
      {galleryImages.length > 1 && (
        <div className="flex gap-3 mt-4 overflow-x-auto pb-2 scrollbar-hide">
          {galleryImages.map((img, idx) => (
            <button
              key={`detail-${idx}`}
              onClick={() => setCurrentIndex(idx)}
              className={`flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentIndex ? 'border-gold ring-2 ring-gold/30' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800'; }} />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox Modal */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
          >
            {/* Close Button */}
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Image Counter */}
            <div className="absolute top-6 left-6 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg z-10">
              <span className="text-white">{currentIndex + 1} / {galleryImages.length}</span>
            </div>

            {/* Main Image */}
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className={`max-w-[90vw] max-h-[85vh] ${isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
              onClick={(e) => {
                e.stopPropagation();
                setIsZoomed(!isZoomed);
              }}
            >
              <img
                src={galleryImages[currentIndex]}
                alt={`${title} - Fullscreen ${currentIndex + 1}`}
                className={`max-w-full max-h-[85vh] object-contain transition-transform duration-300 ${
                  isZoomed ? 'scale-150' : 'scale-100'
                }`}
              />
            </motion.div>

            {/* Navigation */}
            {galleryImages.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}

            {/* Thumbnail Strip in Lightbox */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 bg-black/50 backdrop-blur-sm p-2 rounded-xl">
              {galleryImages.map((img, idx) => (
                <button
                  key={`detail-${idx}`}
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                  className={`w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                    idx === currentIndex ? 'border-gold' : 'border-transparent opacity-50 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Booking Modal Component
const BookingModal = ({ property, onClose }) => {
  const [form, setForm] = useState({ phone: '', preferred_date: '', preferred_time: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.phone || !form.preferred_date || !form.preferred_time) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/properties/${property.id}/book-viewing`, form);
      toast.success('Viewing request submitted! We\'ll contact you soon.');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book viewing');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-obsidian border border-white/20 rounded-2xl max-w-lg w-full p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-playfair text-2xl font-semibold">Book a Viewing</h3>
          <button onClick={onClose} className="text-text-secondary hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-text-secondary mb-2 block">Phone Number *</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full bg-obsidian border border-white/15 rounded-lg px-4 py-3 text-white focus:border-gold outline-none"
              placeholder="+971 50 123 4567"
              data-testid="booking-phone-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-text-secondary mb-2 block">Preferred Date *</label>
              <input
                type="date"
                value={form.preferred_date}
                onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                className="w-full bg-obsidian border border-white/15 rounded-lg px-4 py-3 text-white focus:border-gold outline-none [color-scheme:dark]"
                data-testid="booking-date-input"
              />
            </div>
            <div>
              <label className="text-sm text-text-secondary mb-2 block">Preferred Time *</label>
              <input
                type="time"
                value={form.preferred_time}
                onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
                className="w-full bg-obsidian border border-white/15 rounded-lg px-4 py-3 text-white focus:border-gold outline-none [color-scheme:dark]"
                data-testid="booking-time-input"
              />
            </div>
          </div>
          <div>
            <label className="text-sm text-text-secondary mb-2 block">Message (Optional)</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full bg-obsidian border border-white/15 rounded-lg px-4 py-3 text-white focus:border-gold outline-none h-24 resize-none"
              placeholder="Any specific requirements..."
              data-testid="booking-message-input"
            />
          </div>
        </div>

        <div className="flex gap-4 mt-8">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/20">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="flex-1 bg-gold text-black hover:bg-gold-light"
          >
            {loading ? 'Submitting...' : 'Book Viewing'}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Property Detail Page
const PropertyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBooking, setShowBooking] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const fetchProperty = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      setProperty(response.data);
    } catch (error) {
      toast.error('Property not found');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchProperty();
  }, [fetchProperty]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  const handleSave = async () => {
    try {
      if (isSaved) {
        await axios.delete(`${API}/wishlist/${property.id}`);
        setIsSaved(false);
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist/add`, {
          item_type: 'property',
          item_id: property.id,
          title: property.title
        });
        setIsSaved(true);
        toast.success('Added to wishlist!');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please log in to save items');
        navigate('/login');
      } else {
        toast.error('Failed to update wishlist');
      }
    }
  };

  // Check if property is in wishlist on load
  useEffect(() => {
    const checkWishlist = async () => {
      if (property?.id) {
        try {
          const response = await axios.get(`${API}/wishlist/check/${property.id}`);
          setIsSaved(response.data.in_wishlist);
        } catch {
          /* unauthenticated visitors silently skip the wishlist check */
        }
      }
    };
    checkWishlist();
  }, [property?.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold text-xl">Loading property details...</div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Property not found</h2>
          <Button onClick={() => navigate('/properties')} className="bg-gold text-black">
            Back to Properties
          </Button>
        </div>
      </div>
    );
  }

  const amenityIcons = {
    'Pool': '🏊',
    'Gym': '💪',
    'Parking': '🚗',
    'Security': '🛡️',
    'Garden': '🌳',
    'Beach Access': '🏖️',
    'Smart Home': '🏠',
    'Concierge': '🛎️',
    'Spa': '💆',
    'Cinema': '🎬',
  };

  return (
    <div className="min-h-screen bg-obsidian text-text-primary mobile-pb">
      {/* Navigation */}
      <Nav showBack activePage="/properties" rightContent={
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={handleShare} className="text-text-secondary hover:text-gold">
            <Share2 className="w-5 h-5" />
          </Button>
          <Button 
            variant="ghost" 
            onClick={handleSave} 
            className={isSaved ? 'text-red-500' : 'text-text-secondary hover:text-red-500'}
          >
            <Heart className={`w-5 h-5 ${isSaved ? 'fill-current' : ''}`} />
          </Button>
        </div>
      } />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-8 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Image Gallery */}
            <ImageGallery images={property.images} title={property.title} />

            {/* Title & Location */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                {property.luxury_score && (
                  <span className="bg-gold/20 text-gold px-3 py-1 rounded-full text-sm font-medium">
                    <Star className="w-4 h-4 inline mr-1" /> Luxury Score: {property.luxury_score}
                  </span>
                )}
                {property.completion_status && (
                  <span className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-sm">
                    {property.completion_status}
                  </span>
                )}
              </div>
              <h1 className="font-playfair text-3xl sm:text-4xl font-semibold mb-4">{property.title}</h1>
              <div className="flex items-center gap-2 text-text-secondary text-base sm:text-lg">
                <MapPin className="w-5 h-5 text-gold flex-shrink-0" />
                <span>{property.location}</span>
                {property.community && <span>• {property.community}</span>}
              </div>
            </div>

            {/* Key Features */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              <div className="glass-card p-4 sm:p-6 text-center">
                <Bed className="w-7 h-7 sm:w-8 sm:h-8 text-gold mx-auto mb-2" />
                <div className="text-xl sm:text-2xl font-bold">{property.bedrooms}</div>
                <div className="text-text-secondary text-xs sm:text-sm">Bedrooms</div>
              </div>
              <div className="glass-card p-4 sm:p-6 text-center">
                <Bath className="w-7 h-7 sm:w-8 sm:h-8 text-gold mx-auto mb-2" />
                <div className="text-xl sm:text-2xl font-bold">{property.bathrooms}</div>
                <div className="text-text-secondary text-xs sm:text-sm">Bathrooms</div>
              </div>
              <div className="glass-card p-4 sm:p-6 text-center">
                <Maximize className="w-7 h-7 sm:w-8 sm:h-8 text-gold mx-auto mb-2" />
                <div className="text-xl sm:text-2xl font-bold">{property.built_area?.toLocaleString()}</div>
                <div className="text-text-secondary text-xs sm:text-sm">Sq. Ft.</div>
              </div>
              <div className="glass-card p-4 sm:p-6 text-center">
                <Building2 className="w-7 h-7 sm:w-8 sm:h-8 text-gold mx-auto mb-2" />
                <div className="text-xl sm:text-2xl font-bold capitalize">{property.property_type}</div>
                <div className="text-text-secondary text-xs sm:text-sm">Type</div>
              </div>
            </div>

            {/* Description */}
            <div className="glass-card p-5 sm:p-8">
              <h2 className="font-playfair text-xl sm:text-2xl font-semibold mb-4">About This Property</h2>
              <p className="text-text-secondary leading-relaxed">{property.description}</p>
            </div>

            {/* Property Details */}
            <div className="glass-card p-5 sm:p-8">
              <h2 className="font-playfair text-xl sm:text-2xl font-semibold mb-6">Property Details</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                <div>
                  <div className="text-text-secondary text-sm mb-1">Property Type</div>
                  <div className="font-semibold capitalize">{property.property_type}</div>
                </div>
                <div>
                  <div className="text-text-secondary text-sm mb-1">Built Area</div>
                  <div className="font-semibold">{property.built_area?.toLocaleString()} sq ft</div>
                </div>
                {property.plot_size && (
                  <div>
                    <div className="text-text-secondary text-sm mb-1">Plot Size</div>
                    <div className="font-semibold">{property.plot_size?.toLocaleString()} sq ft</div>
                  </div>
                )}
                {property.floor && (
                  <div>
                    <div className="text-text-secondary text-sm mb-1">Floor</div>
                    <div className="font-semibold">{property.floor}</div>
                  </div>
                )}
                {property.view_type && (
                  <div>
                    <div className="text-text-secondary text-sm mb-1">View</div>
                    <div className="font-semibold capitalize">{property.view_type}</div>
                  </div>
                )}
                <div>
                  <div className="text-text-secondary text-sm mb-1">Furnished</div>
                  <div className="font-semibold">{property.furnished ? 'Yes' : 'No'}</div>
                </div>
                {property.developer && (
                  <div>
                    <div className="text-text-secondary text-sm mb-1">Developer</div>
                    <div className="font-semibold">{property.developer}</div>
                  </div>
                )}
                <div>
                  <div className="text-text-secondary text-sm mb-1">Smart Home</div>
                  <div className="font-semibold">{property.smart_home_ready ? 'Ready' : 'No'}</div>
                </div>
              </div>
            </div>

            {/* Amenities */}
            {property.amenities?.length > 0 && (
              <div className="glass-card p-5 sm:p-8">
                <h2 className="font-playfair text-xl sm:text-2xl font-semibold mb-6">Amenities</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                  {property.amenities.map((amenity, idx) => (
                    <div key={`detail-${idx}`} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <span className="text-xl sm:text-2xl">{amenityIcons[amenity] || '✓'}</span>
                      <span className="text-sm sm:text-base">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Location Map Placeholder */}
            <div className="glass-card p-5 sm:p-8">
              <h2 className="font-playfair text-xl sm:text-2xl font-semibold mb-6">Location</h2>
              <div className="aspect-video bg-white/5 rounded-xl flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-12 h-12 text-gold mx-auto mb-3" />
                  <p className="text-text-secondary">{property.location}</p>
                  {property.community && <p className="text-text-secondary">{property.community}, Dubai</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Price Card */}
              <div className="glass-card p-5 sm:p-8 border border-gold/30">
                <div className="text-text-secondary mb-2">Sale Price</div>
                <div className="font-playfair text-3xl sm:text-4xl font-bold text-gold mb-4">
                  <PriceDisplay amount={property.price} compact={false} className="text-3xl sm:text-4xl font-bold text-gold" />
                </div>
                
                {/* Rental Price Section */}
                {property.available_for_rent && (property.rental_price_monthly || property.rental_price_yearly) && (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-4">
                    <div className="text-emerald-400 text-sm font-semibold mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Rental Available
                    </div>
                    {property.rental_price_monthly && (
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-text-secondary text-sm">Monthly</span>
                        <span className="text-emerald-400 font-bold">{formatPrice(property.rental_price_monthly)}</span>
                      </div>
                    )}
                    {property.rental_price_yearly && (
                      <div className="flex justify-between items-center">
                        <span className="text-text-secondary text-sm">Yearly</span>
                        <span className="text-emerald-400 font-bold">{formatPrice(property.rental_price_yearly)}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {property.investment_score && (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-emerald-400 mb-1">
                      <Sparkles className="w-4 h-4" />
                      <span className="font-semibold">Investment Score</span>
                    </div>
                    <div className="text-2xl font-bold text-emerald-400">{property.investment_score}/100</div>
                  </div>
                )}

                <Button 
                  onClick={() => setShowBooking(true)} 
                  className="w-full bg-gold text-black hover:bg-gold-light py-6 text-lg mb-4"
                  data-testid="book-viewing-btn"
                >
                  <Calendar className="mr-2 w-5 h-5" /> Book a Viewing
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full border-white/20 hover:border-gold py-6"
                  onClick={() => navigate('/concierge')}
                >
                  <Phone className="mr-2 w-5 h-5" /> Contact Agent
                </Button>
              </div>

              {/* Agent Card */}
              {property.assigned_partner_name && (
                <div className="glass-card p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center">
                      <span className="text-2xl font-bold text-gold">
                        {property.assigned_partner_name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="font-semibold">{property.assigned_partner_name}</div>
                      <div className="text-text-secondary text-sm">Property Agent</div>
                    </div>
                  </div>
                  <Button variant="outline" className="w-full border-white/20 hover:border-gold">
                    <Mail className="mr-2 w-4 h-4" /> Send Message
                  </Button>
                </div>
              )}

              {/* Quick Facts */}
              <div className="glass-card p-6">
                <h3 className="font-semibold mb-4">Quick Facts</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Price per sq ft</span>
                    <span className="font-semibold">
                      {formatPrice(property.built_area ? Math.round(property.price / property.built_area) : 0, { showDual: false })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Community</span>
                    <span className="font-semibold">{property.community || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Listed</span>
                    <span className="font-semibold">
                      {property.created_at ? new Date(property.created_at).toLocaleDateString() : 'Recently'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Virtual Tour Section */}
              <VirtualTourPreview propertyId={id} />
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <AnimatePresence>
        {showBooking && <BookingModal property={property} onClose={() => setShowBooking(false)} />}
      </AnimatePresence>
    </div>
  );
};

export default PropertyDetailPage;
